import * as Sentry from '@sentry/browser'

// Handle global errors
const handleError = event => Sentry.captureException(event.error)

// Handle global unhandled promise rejections
const handleRejection = event => Sentry.captureException(event.reason)

// Update the Sentry user context
const updateSentryUser = () => {
  const user = document.querySelector('meta[name="current-user"]')
  Sentry.setUser(user ? { id: user.getAttribute('content') } : null)
}

/**
 * Creates a memoized function to ensure handlers are only set once
 */
function createGlobalHandlers() {
  let handlersInitialized = false

  return () => {
    if (!handlersInitialized) {
      window.addEventListener('error', handleError)
      window.addEventListener('unhandledrejection', handleRejection)
      document.addEventListener('turbo:load', updateSentryUser)

      // Assign the current user to Sentry
      updateSentryUser() // set initial Sentry User state

      handlersInitialized = true
    }
  }
}
const setupEventHandlers = createGlobalHandlers()

/**
 * Setup error handling for Stimulus application
 * @param {Application} application - Stimulus application instance
 * @param {Object} options - Configuration options
 * @param {string} options.sentryDsn - Sentry DSN
 *
 * Usage:
 * setupErrorHandling(application, {
 *  sentryDsn: 'your-dsn'
 * })
 *
 */
export function setupErrorHandling(application, { sentryDsn } = {}) {
  if (import.meta.env.MODE === 'production') {
    if (!sentryDsn) {
      console.warn('Sentry DSN is missing. Error handling will not be initialized.')
      return application
    }

    Sentry.init({ dsn: sentryDsn })

    // Capture global errors and unhandled promise rejections
    setupEventHandlers()

    application.handleError = (error, message, detail = {}) => {
      console.error(message, detail)
      Sentry.captureException(error, {
        extra: {
          controller: detail.identifier ?? 'unknown',
          element: detail.element ?? 'unknown',
          event: detail.event?.type ?? 'unknown',
        }
      })
    }
  } else {
    application.handleError = (error, message, detail) => {
      console.error(message, detail)
      throw error
    }
  }

  return application
}
